import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Icon, Text } from '@kognitos/lattice';
import type { ListInputProps } from '../types';

/** Compares two element lists positionally. */
const sameItems = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((item, index) => item === b[index]);

/**
 * Generic editor for `List[T]`.
 *
 * Renders one element editor per item plus add/remove controls. The element
 * editor itself is supplied by the caller via `renderElement`, so this
 * component never needs to know `T` — the same `ValueType` → component mapping
 * that picks a top-level editor picks the element editor, which is what keeps
 * `List[file]` from needing a rule of its own in the type layer.
 *
 * Only one level of nesting is supported: `T` is always a leaf (scalar or
 * enum). Lists of dictionaries, tables or other lists fall back to the JSON
 * textarea before reaching this component.
 *
 * Rows the user hasn't filled in yet are held locally and excluded from the
 * emitted value. Emitting them would convert a blank string with the element
 * type and produce a concrete wrong value — `''` becomes numeric 0 for
 * `List[number]`, or a field-less Value for a date — which then passes the
 * empty check because the list itself is non-empty.
 *
 * @param props - Component props
 * @returns JSX element containing the element rows and add control
 */
export function ListInput({
  name,
  items,
  onChange,
  renderElement,
  disabled,
  itemLabel,
}: ListInputProps) {
  const { t } = useTranslation('automation-inputs');

  // What's on screen, blank rows included.
  const [draft, setDraft] = useState<string[]>(items);
  // The last list handed to `onChange`, i.e. what `items` should echo back.
  const emittedRef = useRef<string[]>(items);

  // Adopt `items` only when it changes for a reason other than our own emit —
  // a restored default, or Clear all. Without this guard the echo of our own
  // filtered emit would delete the blank row the user is typing into.
  // eslint-disable-next-line no-restricted-syntax -- reason: re-syncs local draft rows when the value is replaced from outside
  useEffect(() => {
    if (!sameItems(items, emittedRef.current)) {
      emittedRef.current = items;
      setDraft(items);
    }
  }, [items]);

  /**
   * Records the new on-screen rows and emits the ones that carry a value.
   *
   * @param next - The full set of rows, blanks included
   */
  const commit = (next: string[]) => {
    setDraft(next);
    const filled = next.filter(item => item.trim() !== '');
    emittedRef.current = filled;
    onChange(filled);
  };

  /**
   * Replaces a single element, leaving the rest of the list untouched.
   *
   * @param index - Position of the element being edited
   * @param value - The element's new display value
   */
  const handleElementChange = (index: number, value: string) => {
    const next = [...draft];
    next[index] = value;
    commit(next);
  };

  /**
   * Removes the element at the given position.
   *
   * @param index - Position of the element to remove
   */
  const handleRemove = (index: number) => {
    commit(draft.filter((_, itemIndex) => itemIndex !== index));
  };

  /** Appends a new blank row. It stays out of the value until filled in. */
  const handleAdd = () => {
    setDraft([...draft, '']);
  };

  return (
    <Flex direction="column" gap={2} className="w-full">
      {draft.length === 0 && (
        <Text level="small" color="muted" data-testid={`list-empty-${name}`}>
          {t('list.empty')}
        </Text>
      )}

      {draft.map((item, index) => (
        // Index-keyed on purpose: elements are positional and have no stable
        // identity of their own, and add/remove always rebuilds the array.
        <Flex key={index} gap={2} align="center" className="w-full">
          <div className="flex-1 min-w-0">
            {renderElement({
              index,
              value: item,
              onChange: value => handleElementChange(index, value),
            })}
          </div>
          {!disabled && (
            <Button
              variant="ghost"
              size="icon"
              className="size-8 shrink-0"
              onClick={() => handleRemove(index)}
              aria-label={t('list.removeItem', { index: index + 1 })}
              data-testid={`list-remove-${name}-${index}`}
            >
              <Icon type="X" className="size-4" />
            </Button>
          )}
        </Flex>
      ))}

      {!disabled && (
        <Button
          variant="outline"
          size="sm"
          className="self-start"
          onClick={handleAdd}
          data-testid={`list-add-${name}`}
        >
          <Icon type="Plus" className="size-4" />
          {itemLabel
            ? t('list.addNamedItem', { item: itemLabel })
            : t('list.addItem')}
        </Button>
      )}
    </Flex>
  );
}
